/**
 * CRM backend REST paths — aligned with OpenAPI on the call-center backend.
 */
export const API_ROUTES = {
    users: '/user-management/api/users',
    usersProfile: '/api/users/profile',
    forceLogout: (username) => `/api/users/${encodeURIComponent(username)}/force-logout`,
    provinces: '/api/provinces',
    districts: '/api/districts',
    villages: '/api/villages',
    branches: '/api/branches',
    countries: '/api/countries',
    sequences: '/api/sequences',
    leads: '/api/leads',
    leadAssignments: '/api/lead-assignments',
    leadUploadBatches: '/api/lead-upload-batches',
    leadUploadErrors: '/api/lead-upload-errors',
    blockLists: '/api/block-lists',
    callsAttempts: '/api/calls/attempts',
    callsSatisfactions: '/api/calls/satisfactions',
    cdrs: '/api/cdrs',
    queueLogsLatest: '/api/queue-logs/latest',
    /** CRM call-center user activation profiles (`crm-backend` — POST/GET `/api/users`). */
    userActivations: '/api/users',
    crmDepartments: '/api/crm/departments',
    coreBankingAccounts: '/api/crm/core-banking/accounts',
    /** Finlevia mobile wallet / partner 360 (agent panel) — IAM host {@link BACKEND_URL}. */
    finleviaCustomer: {
        search: (partnerId) => `/api/v1/call-center/customers/search/${partnerId}`,
        wallet: (partnerId) => `/api/v1/call-center/customers/wallet/${partnerId}`,
        cards: (partnerId) => `/cms/api/cards?customerReference.equals=${partnerId}&fundingReference.equals=${partnerId}`,
        transactions: (partnerId) => `/api/v1/call-center/transactions/partner/${partnerId}`,
    },
    issues: '/api/issues',
    phones: '/api/phones',
    kases: '/api/kases',
    kasesDashboard: '/api/kases/dashboard',
    caseAssignments: '/api/case-assignments',
    caseTransfers: '/api/case-transfers',
    caseTransferWorkflow: {
        request: (caseId) => `/api/case-transfers/workflow/cases/${caseId}/request`,
        approve: (id) => `/api/case-transfers/workflow/${id}/approve`,
        reject: (id) => `/api/case-transfers/workflow/${id}/reject`,
        cancel: (id) => `/api/case-transfers/workflow/${id}/cancel`,
        cancelByAssignment: '/api/case-transfers/workflow/cancel-by-assignment',
    },
    caseUpdates: '/api/case-updates',
    campaigns: '/api/campaigns',
    campaignsDashboard: '/api/campaigns/dashboard',
    campaignCustomers: (campaignId) => `/api/campaigns/${campaignId}/customers`,
    campaignAssign: (campaignId) => `/api/campaigns/${campaignId}/assign`,
    breaks: '/api/breaks',
    breaksActive: '/api/breaks/active',
    breaksEnd: '/api/breaks/end',
    breaksEndForUser: '/api/breaks/end-for-user',
    phonePause: '/api/phone-pause',
    phonePauseStatus: '/api/phone-pause/status',
    phonePauseClear: '/api/phone-pause/clear',
    /** Member-scoped live chat (inbox, history, send, read, DM) — STOMP topics are separate. */
    chat: {
        rooms: '/api/chat-rooms',
        mine: '/api/chat-rooms/mine',
        room: (roomId) => `/api/chat-rooms/${roomId}`,
        roomMessages: (roomId) => `/api/chat-rooms/${roomId}/messages`,
        roomMessage: (roomId, messageId) => `/api/chat-rooms/${roomId}/messages/${messageId}`,
        roomRead: (roomId) => `/api/chat-rooms/${roomId}/read`,
        roomMembers: (roomId) => `/api/chat-rooms/${roomId}/members`,
        roomMember: (roomId, userId) => `/api/chat-rooms/${roomId}/members/${encodeURIComponent(userId)}`,
        dm: (otherUserId) => `/api/chat-rooms/dm/${encodeURIComponent(otherUserId)}`,
        join: (roomId) => `/api/chat-rooms/${roomId}/join`,
    },
    /** Admin CRUD for rooms/members (permissions). */
    chatRooms: '/api/chat-rooms',
    files: '/api/files',
    fileUpload: '/api/files/upload',
    fileDownload: (id) => `/api/files/${id}/download`,
    /** Agent call greeting metadata — `GET ?category=grading&uploadedBy={email}` */
    fileMetadataByCategory: '/api/files/by',
    /** Agent call greeting — `GET ?category=grading&uploadedBy={email}` */
    fileDownloadByCategory: '/api/files/by/download',
    airplaneTickets: {
        base: '/api/airplane-tickets',
        one: (id) => `/api/airplane-tickets/${id}`,
        offers: (id) => `/api/airplane-tickets/${id}/offers`,
        invoices: (id) => `/api/airplane-tickets/${id}/invoices`,
        finalApproval: (id) => `/api/airplane-tickets/${id}/final-approval`,
        approveCancellation: (id) => `/api/airplane-tickets/${id}/approve-cancellation`,
        reschedule: (id) => `/api/airplane-tickets/${id}/reschedule`,
        rejectReschedule: (id) => `/api/airplane-tickets/${id}/reject-reschedule`,
        rejectIssuance: (id) => `/api/airplane-tickets/${id}/reject-issuance`,
        rejectCancellation: (id) => `/api/airplane-tickets/${id}/reject-cancellation`,
        file: (id, fileType) => `/api/airplane-tickets/${id}/files/${encodeURIComponent(fileType)}`,
    },
    reportsExport: (reportName) => `/api/reports/${encodeURIComponent(reportName)}/export`,
    reports: {
        kpiDashboard: '/api/reports/kpi/dashboard',
    },
    qstat: {
        dashboard: '/api/qstat/dashboard',
        voicemails: '/api/qstat/voicemails',
        voicemailDownload: (id) => `/api/qstat/voicemails/${id}/download`,
        incomingCalls: '/api/qstat/incoming-calls',
        outgoingCalls: '/api/qstat/outgoing-calls',
        queueReports: '/api/qstat/queue-reports',
        agentProductivity: '/api/qstat/agent-productivity',
        /** Same query params as `GET /api/qstat/agent-productivity` (not yet in deployed OpenAPI; CRM `SessionReportRestController`). */
        sessionReport: '/api/qstat/session-reports',
        agentStatistics: (username) => `/api/qstat/agents/${encodeURIComponent(username)}/statistics`,
        realtime: '/api/qstat/realtime',
        phoneUsers: '/api/qstat/phone-users',
        wallboard: '/api/qstat/wallboard',
        blacklist: '/api/qstat/blacklist',
        blacklistEntry: (id) => `/api/qstat/blacklist/${id}`,
        callRecordingMetadata: (uniqueid) => `/api/qstat/call-recordings/${encodeURIComponent(uniqueid)}/metadata`,
        callRecordingStream: (uniqueid) => `/api/qstat/call-recordings/${encodeURIComponent(uniqueid)}/stream`,
        callRecordingDownload: (uniqueid) => `/api/qstat/call-recordings/${encodeURIComponent(uniqueid)}/download`,
    },
    /** Legacy / IAM — not in CRM OpenAPI; kept for settings screens until backend exposes equivalents. */
    legacy: {
        roles: '/api/roles',
        permissions: '/api/permissions',
        /** CRM home analytics — compose from `/api/kases` or future report API. */
        crmDashboard: '/api/dashboard',
    },
};
